import React, { useState } from "react";
import { filterJobs, groupJobsByNiche } from "../../../core/data/jobs";
import { formatDate } from "../../../core/data/marketOverviewText";
import { AUDITS } from "../../../core/theme/auditsTheme";
import SnapshotRow from "./SnapshotRow";

// The Audits page's right column: one search box (name, tag or keyword, or a
// website), the snapshots that saw that website, then every snapshot grouped
// by niche. Each niche group can be collapsed.
// website: from useWebsiteSearch ({ domain, searching, jobIds }).
export default function SnapshotsList({ jobs, jobsLoading, jobSearch, setJobSearch, website, onOpenJob, onOpenPinned, onStopJob, onNewScan }) {
  const [collapsed, setCollapsed] = useState(() => new Set());
  const filtered = filterJobs(jobs, jobSearch);
  const groups = groupJobsByNiche(filtered);

  const toggle = key => setCollapsed(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <section className={AUDITS.main}>
      <div className={AUDITS.titleRow}>
        <h1 className={AUDITS.title}>Snapshots</h1>
        {!jobsLoading && jobs.length > 0 && <span className={AUDITS.titleCount}>{jobs.length}</span>}
      </div>

      {jobsLoading ? (
        <p className={AUDITS.message}>Loading snapshots…</p>
      ) : jobs.length === 0 ? (
        <div className={AUDITS.message}>
          <p>No snapshots yet.</p>
          <button type="button" onClick={onNewScan} className={AUDITS.startButton}>Start search</button>
        </div>
      ) : (
        <>
          <input
            className={AUDITS.search} value={jobSearch} onChange={e => setJobSearch(e.target.value)}
            placeholder="Search by name, tag or advertiser website…" aria-label="Search snapshots"
          />
          {website.domain && <WebsiteMatches jobs={jobs} website={website} onOpenPinned={onOpenPinned} />}

          {groups.length === 0 ? (
            !website.domain && <p className={AUDITS.message}>No snapshots match your search.</p>
          ) : (
            <div className={AUDITS.groups}>
              {groups.map(group => {
                const open = !collapsed.has(group.key);
                return (
                  <div key={group.key}>
                    <button type="button" onClick={() => toggle(group.key)} className={AUDITS.groupHead} aria-expanded={open}>
                      <span className={AUDITS.chevron} aria-hidden="true">{open ? "▼" : "▶"}</span>
                      <span className={AUDITS.groupName}>{group.name}</span>
                      <span className={AUDITS.groupCount}>{group.jobs.length}</span>
                    </button>
                    {open && (
                      <div className={AUDITS.rows}>
                        {group.jobs.map(job => <SnapshotRow key={job.id} job={job} onOpen={onOpenJob} onStop={onStopJob} />)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// "acme.co.uk was seen in 3 snapshots", each opening Advertiser Snapshot with
// acme.co.uk pinned. Only snapshots in this account's list are shown.
function WebsiteMatches({ jobs, website, onOpenPinned }) {
  const { domain, searching, jobIds } = website;
  const ids = new Set(jobIds || []);
  const matches = jobs.filter(j => ids.has(j.id));

  return (
    <div className={AUDITS.website} aria-live="polite">
      {searching ? (
        <p className={AUDITS.websiteHead}>Searching for <span className={AUDITS.websiteDomain}>{domain}</span>…</p>
      ) : matches.length === 0 ? (
        <p className={AUDITS.websiteHead}><span className={AUDITS.websiteDomain}>{domain}</span> wasn't seen in any of your snapshots.</p>
      ) : (
        <>
          <p className={`${AUDITS.websiteHead} mb-1.5`}>
            <span className={AUDITS.websiteDomain}>{domain}</span> was seen in {matches.length} {matches.length === 1 ? "snapshot" : "snapshots"}. Click one to open its Advertiser Snapshot.
          </p>
          {matches.map(job => (
            <button key={job.id} type="button" onClick={() => onOpenPinned(job, domain)} className={AUDITS.websiteRow} title={`Open Advertiser Snapshot with ${domain} pinned`}>
              <span className={AUDITS.websiteName}>{job.name || (job.keywords || []).join(", ")}</span>
              <span className={AUDITS.websiteDate}>{formatDate(job.started_at)}</span>
            </button>
          ))}
        </>
      )}
    </div>
  );
}
